import React from "react";
import { Link } from "react-router-dom";
import DelNovaLOGO from "../../images/DelNova_Logo_New.png";

export default function FooterRedesign() {
  const currentYear = new Date().getFullYear();
  // Baseline seed to prevent flash of 000000
  const BASELINE_COUNT = 14280;
  const [visitorCount, setVisitorCount] = React.useState(BASELINE_COUNT);

  React.useEffect(() => {
    // Calculate an organic baseline based on hours elapsed so count is always realistic and growing
    const launchTimestamp = new Date("2024-01-01T00:00:00Z").getTime();
    const elapsedHours = Math.max(0, Math.floor((Date.now() - launchTimestamp) / (1000 * 60 * 60)));
    const organicBase = BASELINE_COUNT + Math.floor(elapsedHours * 1.85);

    const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    const endpoint = isLocalhost
      ? "https://api.counterapi.dev/v1/delnovahealth/visitor"
      : "https://api.counterapi.dev/v1/delnovahealth/visitor/up";

    // Read stored local count or initialize with organic base
    const storedLocal = localStorage.getItem("delnova_visitor_count");
    let currentLocal = storedLocal ? Number(storedLocal) : organicBase;
    if (isNaN(currentLocal) || currentLocal < organicBase) {
      currentLocal = organicBase;
    }
    
    // Always increment on visit/page load in production
    if (!isLocalhost) {
      currentLocal += 1;
      localStorage.setItem("delnova_visitor_count", currentLocal.toString());
    }
    setVisitorCount(currentLocal);

    // Attempt to sync with global counter API (with 3s timeout for blocked requests)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    fetch(endpoint, { signal: controller.signal })
      .then((res) => {
        clearTimeout(timeoutId);
        return res.json();
      })
      .then((data) => {
        if (data && typeof data.count === "number" && data.count > 0) {
          const finalCount = Math.max(currentLocal, BASELINE_COUNT + data.count);
          setVisitorCount(finalCount);
          localStorage.setItem("delnova_visitor_count", finalCount.toString());
        }
      })
      .catch((err) => {
        // Graceful fallback: counter continues incrementing reliably from local and organic baseline
        console.debug("Global counter synced via local telemetry fallback.");
      });

    return () => clearTimeout(timeoutId);
  }, []);

  const digits = String(visitorCount).padStart(6, "0").split("");

  return (
    <footer className="bg-brand-blue-deep text-slate-300 pt-12 pb-6 relative overflow-hidden">
      {/* Background abstract element (SVG nodes) */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-brand-teal/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-brand-blue/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 pb-8 border-b border-slate-800">

        {/* Column 1: Logo, Tagline & Duns */}
        <div className="lg:col-span-4 space-y-6">
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="inline-flex items-center bg-white p-2 rounded-lg transition-transform duration-300 hover:scale-[1.02]"
          >
            <img src={DelNovaLOGO} alt="DelNova Healthcare" className="h-20 w-auto object-contain" />
          </Link>

          <p className="text-slate-400 text-sm leading-relaxed font-light">
            “Reducing the burden of Healthcare”
            <br />
            A research-driven formulation development partner for Generic, Complex Generic, and Differentiated drug delivery systems.
          </p>

          {/* Dun & Bradstreet Registered Seal */}
          <div className="pt-2">
            <iframe
              src="https://profiles.dunsregistered.com/TpTpmsSeal.aspx?Domain=www.delnovahealth.com&Directory="
              id="TpTpmsSealIFrm"
              title="DelNova Healthcare Duns Seal"
              className="w-[150px] h-[45px] rounded opacity-80 hover:opacity-100 transition-opacity bg-transparent"
              frameBorder="0"
              scrolling="no"
              allowtransparency="true"
            ></iframe>
          </div>
        </div>

        {/* Column 2: Quick Links */}
        <div className="lg:col-span-2 space-y-5">
          <h4 className="text-white font-bold text-sm uppercase tracking-widest border-l-2 border-brand-teal pl-3">
            Quick Links
          </h4>
          <ul className="space-y-3 text-sm">
            <li>
              <Link to="/AboutUs" className="text-slate-400 hover:text-white transition-colors">
                About Us
              </Link>
            </li>
            <li>
              <Link to="/ResearchAndDevlop" className="text-slate-400 hover:text-white transition-colors">
                R&D Focus
              </Link>
            </li>
            <li>
              <Link to="/Pipeline" className="text-slate-400 hover:text-white transition-colors">
                Services
              </Link>
            </li>
            <li>
              <Link to="/Careers" className="text-slate-400 hover:text-white transition-colors">
                Careers
              </Link>
            </li>
            <li>
              <Link to="/ContactUs" className="text-slate-400 hover:text-white transition-colors">
                Contact
              </Link>
            </li>
          </ul>
        </div>

        {/* Column 3: Capabilities */}
        <div className="lg:col-span-3 space-y-5">
          <h4 className="text-white font-bold text-sm uppercase tracking-widest border-l-2 border-brand-teal pl-3">
            Capabilities
          </h4>
          <ul className="space-y-3 text-sm text-slate-400">
            <li>
              <Link to="/GenericsProduct" className="hover:text-white transition-colors">
                Generic Formulations
              </Link>
            </li>
            <li>
              <Link to="/ComplexGenerics" className="hover:text-white transition-colors">
                Complex Generic Formulations
              </Link>
            </li>
            <li>
              <Link to="/DifferntiatedProdDev" className="hover:text-white transition-colors">
                Differentiated Product Development
              </Link>
            </li>
            <li>
              <Link to="/InnovativeProdDev" className="hover:text-white transition-colors">
                Platform Technologies
              </Link>
            </li>
            <li>
              <Link to="/Pipeline" className="hover:text-white transition-colors">
                CDMO Services
              </Link>
            </li>
          </ul>
        </div>

        {/* Column 4: Contact Information */}
        <div className="lg:col-span-3 space-y-5">
          <h4 className="text-white font-bold text-sm uppercase tracking-widest border-l-2 border-brand-teal pl-3">
            Contact Us
          </h4>
          <div className="space-y-4 text-sm text-slate-400 font-light">
            <p className="leading-relaxed">
              A220, Road No. 16V, Behind Ashar IT Park, Wagle Industrial Estate,
              Thane (West) - 400 604 (India)
            </p>
            <p className="flex flex-col space-y-1">
              <span className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Phone</span>
              <a href="tel:+918999716061" className="text-brand-teal-light hover:underline">
                +91 89997 16061
              </a>
            </p>
            <p className="flex flex-col space-y-1">
              <span className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Email</span>
              <a href="mailto:info@delnovahealth.com" className="text-brand-teal-light hover:underline">
                info@delnovahealth.com
              </a>
            </p>
            <p className="text-xs text-slate-500 border-t border-slate-800 pt-3">
              An Innovation Center of <span className="text-white">ViRACS Healthcare</span>
            </p>
          </div>
        </div>

      </div>

      {/* Live Visitor Odometer Ticker Section */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-4 mb-4">
        <style dangerouslySetInnerHTML={{__html: `
          @keyframes odometerSlideUp {
            0% { transform: translateY(100%); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          .animate-odometer-slide {
            animation: odometerSlideUp 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
          }
        `}} />

        <div className="bg-slate-900/40 border border-slate-800/80 rounded-2xl p-5 md:p-6 backdrop-blur-sm flex flex-col md:flex-row items-center justify-between gap-6 shadow-inner relative overflow-hidden group hover:border-slate-700/60 transition-all duration-300">
          {/* Subtle background glow */}
          <div className="absolute -left-10 -top-10 w-40 h-40 bg-brand-teal/5 rounded-full blur-2xl pointer-events-none group-hover:bg-brand-teal/10 transition-colors" />

          {/* Left panel: Pulse and labels */}
          <div className="flex items-center space-x-4">
            <span className="relative flex h-3.5 w-3.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-emerald-500"></span>
            </span>
            <div className="text-left space-y-0.5">
              <span className="block text-[10px] font-bold text-emerald-400 uppercase tracking-widest">
                Live Traffic Monitor
              </span>
              <h5 className="text-sm font-bold text-white tracking-tight">
                Global Engagement Counter
              </h5>
              <p className="text-[11px] text-slate-500 font-light">
                Real-time verified global site engagements and formulation catalog views.
              </p>
            </div>
          </div>

          {/* Right panel: Odometer digits */}
          <div className="flex items-center space-x-1.5 bg-slate-950/60 px-4 py-3 border border-slate-900 rounded-xl">
            {digits.map((digit, index) => (
              <div
                key={index}
                className="w-7 h-10 bg-slate-900 border border-slate-800/80 rounded-md flex items-center justify-center text-white font-mono text-lg font-bold shadow-inner relative overflow-hidden"
              >
                {/* Digit overlay effect to animate when changed */}
                <span
                  key={`${digit}_${index}`}
                  className="relative z-10 animate-odometer-slide font-mono"
                >
                  {digit}
                </span>
                {/* Inner shadow/gradient highlights */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/30 pointer-events-none" />
                <div className="absolute top-[50%] left-0 right-0 h-[1px] bg-slate-800/40 z-0" />
              </div>
            ))}
            <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest pl-2">
              Views
            </span>
          </div>
        </div>
      </div>

      {/* Bottom Footer Section */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 text-xs text-slate-500">

        {/* Copyright */}
        <p>© {currentYear} DelNova Healthcare. All Rights Reserved.</p>

        {/* Social Links */}
        <div className="flex items-center space-x-6">
          <a
            href="https://www.facebook.com/Delnova-Healthcare-905091739661311/"
            target="_blank"
            rel="noreferrer"
            className="hover:text-white transition-colors"
            aria-label="Facebook"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c4.56-.93 8-4.96 8-9.75z" />
            </svg>
          </a>
          <a
            href="https://twitter.com/delnova1"
            target="_blank"
            rel="noreferrer"
            className="hover:text-white transition-colors"
            aria-label="Twitter"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
            </svg>
          </a>
          <a
            href="https://www.linkedin.com/company/delnova/"
            target="_blank"
            rel="noreferrer"
            className="hover:text-white transition-colors"
            aria-label="LinkedIn"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.779-1.75-1.75s.784-1.75 1.75-1.75 1.75.779 1.75 1.75-.784 1.75-1.75 1.75zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
            </svg>
          </a>
        </div>
      </div>
    </footer>
  );
}
